import { CommonModule } from '@angular/common';
import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { tap } from 'rxjs';
import { HttpService } from '../../../services/http.service';

interface ApiDataResponse {
    status: boolean;
    message: string;
    data: any;
}

interface CategoryResponse {
    categoryName: string;
    faqCategoryId: string;
    lastUpdatedDisplay: string;
    isActive: boolean | string;
    faQs: FAQResponse[];
}

interface FAQResponse {
    faqId?: string;
    question: string;
    answer: string;
    faqCategoryId: string;
    lastUpdatedDisplay: string;
    isActive?: boolean;
    displayOrder?: number;
}

interface AccordionItem {
    title: string;
    content?: string;
    children?: AccordionItem[];
    expanded?: boolean;
}

interface FAQ {
    id: string;
    question: string;
    answer: string;
    lastUpdatedDisplay: string;
    categoryId: string;
    displayOrder?: number;
}

@Component({
    selector: 'app-accordion',
    standalone: true,
    imports: [CommonModule, MatDialogModule],
    templateUrl: './student-faq.component.html',
    styleUrls: ['./student-faq.component.scss']
})
export class AccordionComponent {
    allFAQs: FAQ[] = [];
    accordionData: AccordionItem[] = [];
    isLoading: boolean = true;

    constructor(
        public dialogRef: MatDialogRef<AccordionComponent>,
        private httpService: HttpService,
        // audienceType: 0 = Student, 1 = Teacher, 2 = CSQ
        @Inject(MAT_DIALOG_DATA) public dialogData: { id?: string; audienceType?: 0 | 1 | 2 }
    ) { }

    ngOnInit(): void {
        this.fetchFAQList().subscribe();
    }

    toggle(item: AccordionItem): void {
        item.expanded = !item.expanded;
    }

    onCancel(): void {
        this.dialogRef.close(false);
    }

    fetchFAQList() {
        this.isLoading = true;
        const audienceParam = this.dialogData?.audienceType !== undefined
            ? `?audienceType=${this.dialogData.audienceType}`
            : '';
        return this.httpService.get<ApiDataResponse>(`faqs/all${audienceParam}`).pipe(
            tap({
                next: (response) => {
                    if (response.status && Array.isArray(response.data)) {
                        const categoryData = response.data as CategoryResponse[];

                        this.accordionData = categoryData.map(category => ({
                            title: category.categoryName,
                            expanded: false,
                            children: category.faQs ? category.faQs.map(faq => ({
                                title: faq.question,
                                content: faq.answer,
                                expanded: false
                            })) : []
                        }));

                        this.allFAQs = categoryData.flatMap(category =>
                            category.faQs ? category.faQs.map(faq => ({
                                id: faq.faqId || '',
                                question: faq.question,
                                answer: faq.answer,
                                categoryId: category.faqCategoryId,
                                lastUpdatedDisplay: faq.lastUpdatedDisplay || category.lastUpdatedDisplay,
                                displayOrder: faq.displayOrder || 0
                            })) : []
                        ).sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));

                    } else {
                        this.accordionData = [];
                        this.allFAQs = [];
                    }
                    this.isLoading = false;
                },
                error: (err) => {
                    console.error('Failed to load FAQs:', err);
                    this.accordionData = [];
                    this.allFAQs = [];
                    this.isLoading = false;
                }
            })
        );
    }
}